/**
 * A2UI Surface Component
 * Manages a single A2UI surface - component tree, data model, and rendering
 * Based on Google A2UI Protocol v0.8
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { componentRegistry } from './ComponentRegistry';
import { dataModelStore } from './DataModelStore';

const A2UISurface = ({ 
  surfaceId,
  components,
  rootId,
  catalogId = 'default',
  styles = {},
  onAction,
  isReady = false
}) => {
  const [dataModel, setDataModel] = useState({});
  const [, forceUpdate] = useState({});

  // Subscribe to data model changes
  useEffect(() => {
    const unsubscribe = dataModelStore.subscribe(surfaceId, (newModel) => {
      setDataModel({ ...newModel });
      forceUpdate({});
    });
    
    // Initialize with current model
    setDataModel(dataModelStore.getSurfaceModel(surfaceId));
    
    return unsubscribe;
  }, [surfaceId]);

  // Create component map for quick lookup
  const componentMap = useMemo(() => {
    const map = new Map();
    components.forEach(comp => {
      map.set(comp.id, comp);
    });
    return map;
  }, [components]);

  // Resolve a BoundValue to actual value
  const resolveValue = useCallback((boundValue, sid, itemContext = null) => {
    return dataModelStore.resolveBoundValue(boundValue, sid || surfaceId, itemContext);
  }, [surfaceId]);

  // Handle data changes from form inputs
  const handleDataChange = useCallback((sid, path, value) => {
    dataModelStore.updateValue(sid || surfaceId, path, value);
  }, [surfaceId]);

  // Handle user actions
  const handleAction = useCallback((action) => {
    // Resolve context values before sending
    const resolvedContext = {};
    if (action.context && Array.isArray(action.context)) {
      action.context.forEach(({ key, value }) => {
        resolvedContext[key] = resolveValue(value, action.surfaceId);
      });
    } else if (action.context && typeof action.context === 'object') {
      Object.entries(action.context).forEach(([key, value]) => {
        resolvedContext[key] = typeof value === 'object' ? resolveValue(value, action.surfaceId) : value;
      });
    }

    const resolvedAction = {
      ...action,
      context: resolvedContext
    };

    if (onAction) {
      onAction(resolvedAction);
    }
  }, [onAction, resolveValue]);

  // Render children (for containers with explicitList or template)
  const renderChildren = useCallback((childrenDef, asList = false) => {
    if (!childrenDef) return null;

    // Handle explicitList
    if (childrenDef.explicitList) {
      const elements = childrenDef.explicitList.map((childId, index) => {
        const rendered = renderComponent(childId);
        if (asList) {
          return <li key={childId || index} className="a2ui-list-item">{rendered}</li>;
        }
        return <React.Fragment key={childId || index}>{rendered}</React.Fragment>;
      });
      return elements;
    }

    // Handle template (for dynamic lists)
    if (childrenDef.template) {
      const { dataBinding, componentId } = childrenDef.template;
      const listData = resolveValue(dataBinding, surfaceId) || [];
      
      if (!Array.isArray(listData)) return null;

      const elements = listData.map((item, index) => {
        const rendered = renderComponent(componentId, item, index);
        if (asList) {
          return <li key={index} className="a2ui-list-item">{rendered}</li>;
        }
        return <React.Fragment key={index}>{rendered}</React.Fragment>;
      });
      return elements;
    }

    return null;
  }, [surfaceId]);

  // Render a single child by ID
  const renderChild = useCallback((childId, itemContext = null) => {
    if (!childId) return null;
    return renderComponent(childId, itemContext);
  }, []);

  // Main component render function
  const renderComponent = useCallback((componentId, itemContext = null, itemIndex = null) => {
    const componentDef = componentMap.get(componentId);
    if (!componentDef) {
      console.warn(`A2UI: Component "${componentId}" not found in surface "${surfaceId}"`);
      return null;
    }

    const { component } = componentDef;
    if (!component) return null;

    // Get the component type (first key in component object)
    const componentType = Object.keys(component)[0];
    const componentProps = component[componentType];

    // Get the React component from registry
    const ReactComponent = componentRegistry.getComponent(componentType, catalogId);
    if (!ReactComponent) {
      console.warn(`A2UI: Unknown component type "${componentType}"`);
      return (
        <div className="a2ui-unknown-component" style={{ color: 'red', padding: '4px' }}>
          Unknown component: {componentType}
        </div>
      );
    }

    // Build props for the component
    const props = {
      ...componentProps,
      surfaceId,
      componentId,
      catalogId,
      resolveValue: (val, sid) => resolveValue(val, sid, itemContext),
      renderChild: (childId) => renderChild(childId, itemContext),
      renderChildren: (children, asList) => renderChildren(children, asList),
      onDataChange: handleDataChange,
      onAction: handleAction,
      itemContext,
      itemIndex,
    };

    // Apply styles if defined
    if (styles && componentProps.styleId && styles[componentProps.styleId]) {
      props.style = { ...props.style, ...styles[componentProps.styleId] };
    }

    return <ReactComponent key={componentId} {...props} />;
  }, [componentMap, catalogId, surfaceId, styles, resolveValue, renderChild, renderChildren, handleDataChange, handleAction]);

  // Don't render until ready signal received
  if (!isReady) {
    return (
      <div className="a2ui-surface-loading" style={{ padding: '1rem', textAlign: 'center' }}>
        <span className="spinner-border spinner-border-sm me-2" role="status" />
        Loading...
      </div>
    );
  }

  // Render from root component
  if (!rootId) {
    console.warn(`A2UI: No root component specified for surface "${surfaceId}"`);
    return null;
  }

  return (
    <div className="a2ui-surface" data-surface-id={surfaceId}>
      {renderComponent(rootId)}
    </div>
  );
};

export default A2UISurface;
